import { apiClient, adresApi, ensureCsrfCookie } from '../../api/client';
import { VKLADKA } from '../../api/vkladka';

/**
 * АРЕНДА РЕДАКТИРОВАНИЯ И ЛЕНТА ИЗМЕНЕНИЙ — двери сервера `s712`
 * (`docs/05-tasks/kontrakt-s712-aktualnost-arenda.md`). Экран чисел и слов не держит:
 * сроки приходят в `arenda.pravila`, отсчёт — от `server_vremya`, слова отказов — в `message`.
 */
/** Вид ресурса под арендой. Новые виды (бот — `s721`) передаются строкой, без правки этого типа. */
export type TipResursa = 'inquiry' | 'booking' | (string & {});

export interface Arenda {
  tip: TipResursa;
  id: number;
  zanyata: boolean;
  moya: boolean;
  eta_vkladka: boolean;
  istekla: boolean;
  /**
   * Кто редактирует. `imya` — готовая подпись для экрана; под «Войти как» она называет и сидящего
   * за сессией («Юрий под учётной записью «Наталья»»), `uchetka` и `realnyy` — те же сведения порознь.
   */
  vladelets: { id: number; imya: string; uchetka?: string; realnyy?: { id: number; imya: string } | null } | null;
  epoha?: number;
  vzyata_at?: string | null;
  vzyata_display?: string | null;
  istekaet_at?: string | null;
  ostalos_sek?: number | null;
  mozhno_perehvatit: boolean;
  perehvat?: { kem: { id: number; imya: string } | null; u_kogo?: number | null; kogda?: string | null; kogda_display?: string | null } | null;
  pravila: { bezdeystvie_sek: number; preduprezhdenie_sek: number; puls_sek: number; puls_predel_sek: number; strogo?: boolean };
  server_vremya: string;
}

export interface OtvetArendy {
  arenda: Arenda;
  versiya?: string;
  server_vremya?: string;
}

/** Отказ стража и дверей аренды — одна форма на всех адресах. */
export interface OtkazArendy {
  status: number;
  kod: string | null;
  message: string | null;
  arenda: Arenda | null;
}

export function razobratOtkaz(e: unknown): OtkazArendy {
  const otvet = (e as { response?: { status?: number; data?: { kod?: string; message?: string; arenda?: Arenda } } })?.response;

  return {
    status: otvet?.status ?? 0,
    kod: otvet?.data?.kod ?? null,
    message: otvet?.data?.message ?? null,
    arenda: otvet?.data?.arenda ?? null,
  };
}

async function hodArendy(tip: TipResursa, id: number, hod: string, telo: Record<string, unknown> = {}): Promise<OtvetArendy> {
  // Метка защиты от подделки запроса у вошедшего человека уже есть; лишний запрос за ней на каждый
  // пульс не шлём — только когда её нет.
  if (!/(?:^|;\s*)XSRF-TOKEN=/.test(document.cookie)) await ensureCsrfCookie();
  const { data } = await apiClient.post<OtvetArendy>(`/aktualnost/arenda/${tip}/${id}/${hod}`, telo);

  return data;
}

export const vzyatArendu = (tip: TipResursa, id: number, perenesti = false) => hodArendy(tip, id, 'vzyat', perenesti ? { perenesti: true } : {});
export const pulsArendy = (tip: TipResursa, id: number, aktivnost: boolean) => hodArendy(tip, id, 'puls', { aktivnost });
export const perehvatitArendu = (tip: TipResursa, id: number) => hodArendy(tip, id, 'perehvat');

/** Состояние аренды и версия ресурса — только чтение, аренду не трогает. */
export async function sostoyanieResursa(tip: TipResursa, id: number): Promise<OtvetArendy> {
  const { data } = await apiClient.get<OtvetArendy>(`/aktualnost/resurs/${tip}/${id}`);

  return data;
}

/**
 * Отпустить при закрытии карточки или окна. ⛔ Не `sendBeacon` (он не несёт заголовков) и не
 * обычный запрос (страница может закрыться раньше ответа): `fetch` с `keepalive`. Не дошло —
 * аренда освободится сама через минуту без пульса.
 */
export function otpustitArendu(tip: TipResursa, id: number, doSmenyUchetki = false): Promise<void> {
  const xsrf = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
  const controller = doSmenyUchetki ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 5000) : undefined;

  const zapros = fetch(adresApi(`/aktualnost/arenda/${tip}/${id}/otpustit`), {
    method: 'POST',
    keepalive: true,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-Vkladka': VKLADKA,
      ...(xsrf ? { 'X-XSRF-TOKEN': decodeURIComponent(xsrf[1]) } : {}),
    },
    body: '{}',
    ...(controller ? { signal: controller.signal } : {}),
  }).then(async (otvet) => {
    if (doSmenyUchetki && !otvet.ok) {
      const data = await otvet.json().catch(() => ({}));
      throw Object.assign(new Error('lease_release_failed'), { response: { status: otvet.status, data } });
    }
  }).finally(() => clearTimeout(timer));

  // Закрывающаяся вкладка не ждёт сеть. Перед сменой сессии отказ должен остановить переход.
  return doSmenyUchetki ? zapros : zapros.catch(() => undefined);
}

export interface IzmenenieResursa {
  tip: string;
  id: number;
  sled: number;
  deystvie?: string;
  kogda?: string;
}

export interface OtvetLenty {
  kursor: number;
  polnoe: boolean;
  opros_sek: number;
  server_vremya: string;
  resursy: IzmenenieResursa[];
  /** Слова экрана из словаря сервера (если сервер их шлёт). */
  slova?: Record<string, string>;
}

export async function fetchIzmeneniya(posle: number | null): Promise<OtvetLenty> {
  const { data } = await apiClient.get<OtvetLenty>('/aktualnost/izmeneniya', { params: posle === null ? {} : { posle } });

  return data;
}
